import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  registerPartnersQueries,
  type ApiRegistration,
  type ApiOrganization as PartnerRegisterOrganization,
  type ApiStakeholder as PartnerRegisterStakeholder,
  type ApiPartnerRegistration,
} from '@repo/api-kit/queries/register-partners';
import {
  registerOrganizationQueries,
  type ApiOrganization as RegisterOrganization,
} from '@repo/api-kit/queries/register-organization';
import {
  membershipQueries,
  type ApiStakeholder as RegisterStakeholder,
} from '@repo/api-kit/queries/register-memberships';
import { isResponseError, shouldSendToSentry } from '@repo/shared-kit/utils';
import { useSentry } from '@repo/shared-kit/sentry';

interface PrimaryRegistrationData {
  registration: ApiRegistration;
  organization: PartnerRegisterOrganization;
  owner?: PartnerRegisterStakeholder;
}

interface FallbackRegistrationData {
  registration: ApiPartnerRegistration;
  organization: RegisterOrganization;
  owner?: RegisterStakeholder;
}

export type RegistrationWithRelations = PrimaryRegistrationData | FallbackRegistrationData;

function useRegistrationQueryOptions(registrationId: string | undefined) {
  const queryClient = useQueryClient();
  const sentry = useSentry();

  return {
    queryKey: [
      ...registerPartnersQueries.all,
      'registration-with-relations',
      registrationId,
    ] as const,
    queryFn: async (): Promise<RegistrationWithRelations> => {
      if (!registrationId) {
        throw new Error('Registration ID is required');
      }

      try {
        const result = await queryClient.ensureQueryData(
          registerPartnersQueries.registration(registrationId)
        );
        const owner = result.stakeholders.find(s => s.owner === true);

        return {
          registration: result.registration,
          organization: result.organization,
          owner,
        };
      } catch (error: unknown) {
        if (isResponseError(error) && error.status === 404) {
          return fetchFallbackRegistration(queryClient, sentry, registrationId);
        }

        if (shouldSendToSentry(error)) {
          sentry.captureException(error);
        }

        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  };
}

async function fetchFallbackRegistration(
  queryClient: ReturnType<typeof useQueryClient>,
  sentry: ReturnType<typeof useSentry>,
  registrationId: string
): Promise<FallbackRegistrationData> {
  try {
    const partnerReg = await queryClient.ensureQueryData(
      registerPartnersQueries.partnerRegistration(registrationId)
    );

    const orgResult = await queryClient.ensureQueryData(
      registerOrganizationQueries.item(partnerReg.organizationId)
    );

    if (!orgResult.organization) {
      throw new Error(
        `Organization from partner registration ${partnerReg.organizationId} not found`
      );
    }

    const ownerResult = await queryClient.ensureQueryData(
      membershipQueries.item(orgResult.organization.ownerId)
    );

    return {
      registration: partnerReg,
      organization: orgResult.organization,
      owner: ownerResult.stakeholder ?? undefined,
    };
  } catch (fallbackError: unknown) {
    if (shouldSendToSentry(fallbackError)) {
      sentry.captureException(fallbackError);
    }
    throw fallbackError;
  }
}

export function useRegistration(registrationId?: string) {
  const options = useRegistrationQueryOptions(registrationId);
  return useQuery({
    ...options,
    enabled: Boolean(registrationId),
  });
}

export function useSuspenseRegistration(registrationId?: string) {
  if (!registrationId) {
    throw new Error('Registration ID is required for suspense query');
  }

  return useSuspenseQuery(useRegistrationQueryOptions(registrationId));
}
